// src/features/audittrail/RecordsTab.tsx
// The explorer: one filter bar (actor / resource / time range / action /
// classification / text) over four alternate query RPCs that answer the same
// question — "find the records I'm looking for" — plus export.
// ListAuditRecords, ListAuditRecordsByActor, ListAuditRecordsByResource,
// SearchAuditRecords, GetAuditRecord, ExportAuditTrail.

import { useCallback, useEffect, useState } from 'react';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { audittrailStore } from '@core/stores';
import { Button, Input, Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableEmpty, ErrorAlert } from '@react/components';
import { Select } from '@/components/Select';
import { ListAuditRecordsByActor } from '@core/api/endpoints/audittrail/list_audit_records_by_actor';
import { ListAuditRecordsByResource } from '@core/api/endpoints/audittrail/list_audit_records_by_resource';
import { SearchAuditRecords } from '@core/api/endpoints/audittrail/search_audit_records';
import { ExportAuditTrail } from '@core/api/endpoints/audittrail/export_audit_trail';
import type { AuditRecord } from '@core/types/audittrail';
import { RecordDetailRail } from './RecordDetailRail';
import { AUDIT_ACTION_VALUES, DATA_CLASSIFICATION_VALUES, enumKey, fmtDateTime, errMessage, paginationParams } from './logic';

export const RecordsTab = observer(() => {
  const { t, locale } = useI18n();
  const [items, setItems] = useState<AuditRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [actorId, setActorId] = useState('');
  const [resourceUrn, setResourceUrn] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [action, setAction] = useState('');
  const [classification, setClassification] = useState('');
  const [selected, setSelected] = useState<AuditRecord | null>(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const pagination = paginationParams(50);

    // ListAuditRecordsByActor/ByResource only accept pagination — action,
    // classification and date range are applied client-side over the fetched
    // page below so every filter combination behaves consistently regardless
    // of which RPC answered it.
    let req: Promise<{ items?: { items?: AuditRecord[] } }>;
    if (resourceUrn.trim()) {
      req = ListAuditRecordsByResource(resourceUrn.trim(), pagination);
    } else if (actorId.trim()) {
      req = ListAuditRecordsByActor(actorId.trim(), pagination);
    } else if (query.trim() || from || to || action || classification) {
      req = SearchAuditRecords({
        query: query.trim(),
        pagination,
        from: from ? new Date(from).toISOString() : undefined,
        to: to ? new Date(to).toISOString() : undefined,
        action: (action || undefined) as AuditRecord['action'],
        data_classification: (classification || undefined) as AuditRecord['data_classification'],
      });
    } else {
      req = audittrailStore.ListAuditRecords(pagination).then(() => ({ items: { items: audittrailStore.items } }));
    }

    req
      .then((res) => {
        let rows = res.items?.items ?? [];
        if (action) rows = rows.filter((r) => r.action === action);
        if (classification) rows = rows.filter((r) => r.data_classification === classification);
        if (from) rows = rows.filter((r) => !r.occurred_at || new Date(r.occurred_at as unknown as string) >= new Date(from));
        if (to) rows = rows.filter((r) => !r.occurred_at || new Date(r.occurred_at as unknown as string) <= new Date(to));
        setItems(rows);
      })
      .catch((e: unknown) => setError(errMessage(e, t('audittrail.records.loadError'))))
      .finally(() => setLoading(false));
  }, [resourceUrn, actorId, query, from, to, action, classification, t]);

  useEffect(() => { load(); }, [load]);

  const openRow = async (row: AuditRecord) => {
    setSelected(row);
    if (!row.entity?.id) return;
    try {
      const full = await audittrailStore.GetAuditRecord(row.entity.id);
      setSelected(full);
    } catch {
      // Keep the row already shown
    }
  };

  const runExport = async () => {
    setExporting(true);
    setError(null);
    try {
      const res = await ExportAuditTrail({
        from: from ? new Date(from).toISOString() : new Date(0).toISOString(),
        to: to ? new Date(to).toISOString() : new Date().toISOString(),
        resource_urn: resourceUrn.trim() || undefined,
        action: (action || undefined) as AuditRecord['action'],
        data_classification: (classification || undefined) as AuditRecord['data_classification'],
      });
      const bytes = Uint8Array.from(atob(res.data), (c) => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: res.content_type || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = res.filename || 'audit-trail-export';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(errMessage(e, t('audittrail.records.exportError')));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="flex h-full gap-0">
      <div className="min-w-0 flex-1 space-y-4">
        <div className="flex flex-wrap items-end gap-2">
          <Input label={t('audittrail.records.filter.search')} value={query} onChange={(e) => setQuery(e.target.value)} className="w-48" fullWidth={false} />
          <Input label={t('audittrail.records.filter.actor')} value={actorId} onChange={(e) => setActorId(e.target.value)} className="w-40" fullWidth={false} />
          <Input label={t('audittrail.records.filter.resource')} value={resourceUrn} onChange={(e) => setResourceUrn(e.target.value)} className="w-56" fullWidth={false} placeholder="urn:corde:..." />
          <Input type="date" label={t('audittrail.records.filter.from')} value={from} onChange={(e) => setFrom(e.target.value)} className="w-36" fullWidth={false} />
          <Input type="date" label={t('audittrail.records.filter.to')} value={to} onChange={(e) => setTo(e.target.value)} className="w-36" fullWidth={false} />
          <Select
            label={t('audittrail.records.filter.action')}
            value={action}
            onChange={(e) => setAction(e.target.value)}
            className="w-36"
            options={[{ value: '', label: t('common.all') }, ...AUDIT_ACTION_VALUES.map((a) => ({ value: a, label: t(`audittrail.auditAction.${enumKey(a, 'AUDIT_ACTION_')}`) }))]}
          />
          <Select
            label={t('audittrail.records.filter.classification')}
            value={classification}
            onChange={(e) => setClassification(e.target.value)}
            className="w-40"
            options={[{ value: '', label: t('common.all') }, ...DATA_CLASSIFICATION_VALUES.map((c) => ({ value: c, label: t(`audittrail.dataClassification.${enumKey(c, 'DATA_CLASSIFICATION_')}`) }))]}
          />
          <Button size="sm" variant="secondary" onClick={load}>{t('common.filter')}</Button>
          <Button size="sm" variant="secondary" onClick={runExport} isLoading={exporting} className="ml-auto">{t('audittrail.records.export')}</Button>
        </div>

        {error && <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} />}

        <Table hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>{t('audittrail.records.column.event')}</TableHead>
              <TableHead>{t('audittrail.records.column.actor')}</TableHead>
              <TableHead>{t('audittrail.records.column.resource')}</TableHead>
              <TableHead>{t('audittrail.records.column.occurred')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={4} className="py-8 text-center text-muted-foreground">{t('common.loading')}</TableCell></TableRow>
            ) : items.length === 0 ? (
              <TableEmpty colSpan={4} message={t('audittrail.records.empty')} />
            ) : (
              items.map((r) => (
                <TableRow key={r.entity?.id} onClick={() => openRow(r)} className={selected?.entity?.id === r.entity?.id ? 'bg-muted/50' : undefined}>
                  <TableCell className="text-sm">{r.event_type}</TableCell>
                  <TableCell className="text-sm">{r.actor?.display_name || r.actor?.entity?.id || '—'}</TableCell>
                  <TableCell className="max-w-xs truncate font-mono text-xs text-muted-foreground">{r.resource_urn}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{fmtDateTime(r.occurred_at as unknown as string, locale)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {selected && <RecordDetailRail record={selected} onClose={() => setSelected(null)} />}
    </div>
  );
});

export default RecordsTab;
